import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Tab,
  Tabs,
} from '@mui/material';
import dayjs from 'dayjs';
import {t} from 'i18next';
import update from 'immutability-helper';
import _ from 'lodash';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import API, {getMessagesFromApiError} from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import {useAppDispatch, useAppSelector} from '../../../hooks/redux';
import {useRefreshInterval} from '../../../hooks/refreshInterval';
import {
  AmsEmoduleSensorHistoryQuery,
  AmsEmoduleSensorHistoryResponse,
} from '../../../interfaces/AmsEmoduleSensorHistory';
import {
  DashboardEntity,
  DashboardPanelData,
} from '../../../interfaces/Dashboard';
import {ExportField} from '../../../interfaces/Export';
import reduxActions from '../../../redux/actions';
import {
  handleChangeTab,
  handleCloseTab,
  handleOpenTab,
} from '../../../utils/tab';
import {AutoRefreshSelect} from '../../common/AutoRefreshSelect';
import {ResizableColumns} from '../../common/ResizableColumns';
import TabLabel from '../../common/TabLabel';
import {
  usePanel,
  usePanelFilter,
} from '../../dashboards/entities/DashboardEntityContext';
import {AmsEmoduleObjectSelect} from '../../selectors/AmsEmoduleObjectSelect';
import {DateRangeSelect} from '../../selectors/DateRangeSelect';
import {DashboardPanelTitleSlot} from '../DashboardPanelTitleSlot';
import {AmsEmoduleSensorHistoryChart} from './AmsEmoduleSensorHistoryChart';
import {AmsEmoduleSensorHistoryExportButton} from './AmsEmoduleSensorHistoryExportButton';
import {AmsEmoduleSensorHistoryExportModal} from './AmsEmoduleSensorHistoryExportModal';
import {AmsEmoduleSensorHistoryGrid} from './AmsEmoduleSensorHistoryGrid';

export interface AmsEmoduleSensorHistoryTabConfig {
  id?: number;
  refreshInterval?: number;
  selectedIds?: number[];
  shownFields?: string[];
  exportFields?: ExportField[];
  params?: AmsEmoduleSensorHistoryQuery;
  columnWidths?: Record<string, number>;
  columnOrder?: string[];
}

interface AmsEmoduleSensorHistoryConfig {
  activeId?: number;
  openedItems?: AmsEmoduleSensorHistoryTabConfig[];
}

interface AmsEmoduleSensorHistoryData {
  viewType: 'amsEmoduleSensorHistory';
  amsEmoduleSensorHistory: AmsEmoduleSensorHistoryConfig;
}

const getDefaultAmsEmoduleSensorHistoryData =
  (): AmsEmoduleSensorHistoryData => {
    return {
      viewType: 'amsEmoduleSensorHistory',
      amsEmoduleSensorHistory: {},
    };
  };

interface Props {
  value?: DashboardPanelData;
  entities?: DashboardEntity[];
  onUpdate?: (value?: DashboardPanelData) => void;
}

export const AmsEmoduleSensorHistory = (props: Props) => {
  const reduxDispatch = useAppDispatch();

  const isOpenAwayFromConnectView = !props.entities
    ?.map((e) => e.name)
    .includes('Connect View');
  const gridRef = useRef();
  const amsEmodules = useAppSelector(({assets}) => assets.ams_emodules);

  const [panel] = usePanel();

  const [fetchedData, setFetchedData] =
    useState<AmsEmoduleSensorHistoryResponse>();
  const [fetchedErrors, setFetchedErrors] = useState<string[]>([]);
  const [fetchedInProgress, setFetchedInProgress] = useState(false);
  const [exportModalOpened, setExportModalOpened] = useState(false);

  const viewData = _.isEmpty(props.value)
    ? getDefaultAmsEmoduleSensorHistoryData()
    : (props.value as AmsEmoduleSensorHistoryData);
  const [config, setConfig] = useState<AmsEmoduleSensorHistoryConfig>({
    ...viewData.amsEmoduleSensorHistory,
  });

  // We need to track activeId change passed from ConnectView->onOpenHistory handler
  useEffect(() => {
    viewData.amsEmoduleSensorHistory.activeId &&
      viewData.amsEmoduleSensorHistory.openedItems?.length &&
      viewData.amsEmoduleSensorHistory.openedItems?.length > 1 &&
      openTab(viewData.amsEmoduleSensorHistory.activeId);
  }, [viewData.amsEmoduleSensorHistory.activeId]);

  const defaultTabConfig: AmsEmoduleSensorHistoryTabConfig = {
    params: {
      date_start: dayjs().format('YYYY-MM-DD'),
      date_end: dayjs().format('YYYY-MM-DD'),
      limit: 50,
      page: 0,
    },
    columnWidths: {},
    columnOrder: undefined,
  };
  const openedItem = useMemo(
    () =>
      !config.activeId
        ? undefined
        : {
            ...defaultTabConfig,
            ...config.openedItems?.find((it) => it.id === config.activeId),
          },
    [config.openedItems, config.activeId]
  );

  const openedItemIndex = useMemo(
    () => config.openedItems?.findIndex((i) => i.id === openedItem?.id) ?? -1,
    [openedItem]
  );

  const {filter} = usePanelFilter();
  const dashboardDates =
    !props.value?.detachedFromDashboardDate && filter?.params?.date_start
      ? {
          date_start: filter.params.date_start as string,
          date_end: filter.params.date_end as string,
        }
      : null;

  const effectiveOpenedItem = useMemo(() => {
    if (!openedItem || !dashboardDates) {
      return openedItem;
    }
    if (
      openedItem.params?.date_start === dashboardDates.date_start &&
      openedItem.params?.date_end === dashboardDates.date_end
    ) {
      return openedItem;
    }
    return {...openedItem, params: {...openedItem.params, ...dashboardDates}};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openedItem, dashboardDates?.date_start, dashboardDates?.date_end]);

  const fetchData = useCallback(async () => {
    if (!effectiveOpenedItem?.id) {
      return;
    }

    setFetchedInProgress(true);
    setFetchedErrors([]);
    try {
      const resp = await API.get<AmsEmoduleSensorHistoryResponse>(
        `${apiBaseUrl}/ams/emodule/${effectiveOpenedItem.id}/sensor-history`,
        {params: effectiveOpenedItem.params}
      );
      setFetchedData(resp.data);
    } catch (error: any) {
      const messages = getMessagesFromApiError(error, 'STRA_813abb3b');
      setFetchedErrors(messages);
    }
    setFetchedInProgress(false);
  }, [effectiveOpenedItem?.id, effectiveOpenedItem?.params]);

  useEffect(() => {
    if (effectiveOpenedItem?.id) {
      fetchData();
    }
  }, [effectiveOpenedItem?.id, effectiveOpenedItem?.params]);

  useEffect(() => {
    if (openedItem?.id) {
      props?.onUpdate?.(
        update(viewData, {
          amsEmoduleSensorHistory: {
            $set: config,
          },
        })
      );
    }
  }, [openedItem]);

  const openTab = (id: number) => {
    return handleOpenTab(id, config, setConfig);
  };

  const closeTab = (id: number) => {
    return handleCloseTab(id, config, setConfig);
  };

  const changeTab = (_: any, id: number) => {
    handleChangeTab(id, config, setConfig);
  };

  const tabNames = useMemo(
    () =>
      config.openedItems?.map(
        (o) =>
          `E-module #${amsEmodules.find((i) => Number(i.id) === Number(o.id))?.serial_number ?? 'unknown'}`
      ),
    [config.openedItems, amsEmodules]
  );

  // get fresh assets
  useEffect(() => {
    reduxDispatch(reduxActions.assets.fetchAmsEmodules);
  }, [config.openedItems]);

  useRefreshInterval(fetchData, openedItem?.refreshInterval);

  return (
    <>
      <Box
        display="flex"
        flexDirection="column"
        width="100%"
        height="calc(100% - 38px)"
      >
        <DashboardPanelTitleSlot>
          {t(`panels.${panel?.code}`)}
        </DashboardPanelTitleSlot>

        <Backdrop open={fetchedInProgress} sx={{position: 'absolute'}}>
          <CircularProgress color="inherit" />
        </Backdrop>

        {/* Errors */}
        {fetchedErrors.map((error, idx) => (
          <Alert
            key={idx}
            severity="error"
            onClose={() => openedItem && fetchData()}
          >
            {error}
          </Alert>
        ))}

        {/* Filters */}
        <Box display="flex" flexDirection="column" gap={1} py={1}>
          {isOpenAwayFromConnectView && (
            <Box>
              <AmsEmoduleObjectSelect onChange={openTab} />
            </Box>
          )}
          <Box pb={2}>
            {config.openedItems?.length ? (
              <Tabs
                value={openedItem?.id}
                variant="scrollable"
                onChange={changeTab}
              >
                {config.openedItems.map((t, idx) => (
                  <Tab
                    key={t.id}
                    value={t.id}
                    label={
                      <TabLabel
                        name={tabNames?.[idx] ?? ''}
                        onClose={() => t.id && closeTab(t.id)}
                      />
                    }
                  />
                ))}
              </Tabs>
            ) : (
              <Alert severity="warning">No e-module object selected</Alert>
            )}
          </Box>

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box maxWidth={400}>
              <DateRangeSelect
                value={[
                  dayjs(effectiveOpenedItem?.params?.date_start).toDate(),
                  dayjs(effectiveOpenedItem?.params?.date_end).toDate(),
                ]}
                disabled={!openedItem}
                onChange={(v) => {
                  if (openedItem) {
                    const newStart = v?.[0]
                      ? dayjs(v?.[0]).format('YYYY-MM-DD')
                      : undefined;
                    const newEnd = v?.[1]
                      ? dayjs(v?.[1]).format('YYYY-MM-DD')
                      : undefined;
                    const detach =
                      dashboardDates &&
                      (newStart !== dashboardDates.date_start ||
                        newEnd !== dashboardDates.date_end);
                    const newConfig = update(config, {
                      openedItems: {
                        [openedItemIndex]: {
                          $set: {
                            ...openedItem,
                            params: {
                              ...openedItem.params,
                              date_start: newStart,
                              date_end: newEnd,
                            },
                          },
                        },
                      },
                    });
                    setConfig(newConfig);
                    if (detach) {
                      props.onUpdate?.({
                        ...update(viewData, {
                          amsEmoduleSensorHistory: {$set: newConfig},
                        }),
                        detachedFromDashboardDate: true,
                      });
                    }
                  }
                }}
              />
            </Box>

            <Box display="flex">
              <ButtonGroup disabled={!openedItem}>
                <AmsEmoduleSensorHistoryExportButton
                  size="small"
                  disabled={!openedItem}
                  onClick={() => setExportModalOpened(true)}
                />

                <Button size="small" onClick={fetchData}>
                  <RefreshIcon />
                </Button>

                <AutoRefreshSelect
                  value={openedItem?.refreshInterval ?? null}
                  onChange={(v) => {
                    if (openedItem) {
                      setConfig(
                        update(config, {
                          openedItems: {
                            [openedItemIndex]: {
                              $set: {
                                ...openedItem,
                                refreshInterval: v as number,
                              },
                            },
                          },
                        })
                      );
                    }
                  }}
                />

                <Button
                  size="small"
                  onClick={() => {
                    // @ts-ignore
                    gridRef.current.print();
                  }}
                >
                  <PrintIcon />
                </Button>
              </ButtonGroup>
            </Box>
          </Box>
        </Box>

        <ResizableColumns
          left={
            <Box pr="24px" height="100%">
              {/* Grid */}
              <AmsEmoduleSensorHistoryGrid
                ref={gridRef}
                data={fetchedData}
                config={openedItem}
                onChange={(v) => {
                  setConfig(
                    update(config, {
                      openedItems: {
                        [openedItemIndex]: {
                          $set: v,
                        },
                      },
                    })
                  );
                }}
              />
            </Box>
          }
        >
          {/* Chart */}
          <AmsEmoduleSensorHistoryChart
            data={fetchedData}
            config={openedItem}
          />
        </ResizableColumns>
      </Box>

      {exportModalOpened && (
        <AmsEmoduleSensorHistoryExportModal
          value={openedItem}
          opened={exportModalOpened}
          onClose={() => setExportModalOpened(false)}
          onSubmit={(exportFields) => {
            if (openedItem) {
              setConfig(
                update(config, {
                  openedItems: {
                    [openedItemIndex]: {
                      $set: {
                        ...openedItem,
                        exportFields,
                      },
                    },
                  },
                })
              );
            }
            setExportModalOpened(false);
          }}
        />
      )}
    </>
  );
};
